import { AssetGroup } from '../core/asset/AssetGroup';
import { CircleAsset } from '../core/asset/CircleAsset';
import { ColorAsset } from '../core/asset/ColorAsset';
import { GradientAsset } from '../core/asset/GradientAsset';
import { IconAsset } from '../core/asset/IconAsset';
import { LottieAsset } from '../core/asset/LottieAsset';
import { RectangleAsset } from '../core/asset/RectangleAsset';
import { TimeAwareMediaAsset } from '../core/asset/TimeAwareMediaAsset';
import { GraphicStyleTextAsset } from '../core/text/GraphicStyleTextAsset';
import type { GetAssetType, AssetType } from '../core/asset/Container';
import { TextAsset } from '../core/text/TextAsset';
import { assertHasOwnProperties, assertInEnum, assertObject } from '../validators';
import { Config } from '../video-data/config';
import { CanvasTextAsset } from '../core/canvasText/asset/CanvasTextAsset';
import { CanvasFancyCaptionAsset } from '../core/canvasFancyCaption/asset/CanvasFancyCaptionAsset';


function getAssetForType<T extends AssetType>(assetType: T, parent, settings): GetAssetType<T> {
    if (parent.root.enableCanvasText && (assetType === Config.TEXT_ASSET_TYPE || assetType === 'TextAsset')) {
        assetType = Config.CANVAS_TEXT_ASSET_TYPE as T;
    }
    switch (assetType) {
        case Config.GROUP_ASSET_TYPE: return new AssetGroup(parent, settings) as GetAssetType<T>;
        case Config.COLOR_ASSET_TYPE: return new ColorAsset(parent, settings) as GetAssetType<T>;
        case Config.CIRCLE_ASSET_TYPE: return new CircleAsset(parent, settings) as GetAssetType<T>;
        case Config.GRADIENT_ASSET_TYPE: return new GradientAsset(parent, settings) as GetAssetType<T>;
        case Config.RECTANGLE_ASSET_TYPE: return new RectangleAsset(parent, settings) as GetAssetType<T>;
        case 'TextAsset': return new TextAsset(parent, settings) as GetAssetType<T>;
        case Config.TEXT_ASSET_TYPE: return new GraphicStyleTextAsset(parent, settings) as GetAssetType<T>;
        case Config.CANVAS_TEXT_ASSET_TYPE: return new CanvasTextAsset(parent, settings) as GetAssetType<T>;
        case Config.CANVAS_FANCY_CAPTION_TYPE: return new CanvasFancyCaptionAsset(parent, settings) as GetAssetType<T>;
        case Config.MEDIA_ASSET_TYPE: return new TimeAwareMediaAsset(parent, settings) as GetAssetType<T>;
        case Config.LOTTIE_ASSET_TYPE: return new LottieAsset(parent, settings) as GetAssetType<T>;
        case Config.ICON_ASSET_TYPE: return new IconAsset(parent, settings) as GetAssetType<T>;
        default:
            throw Error(`Unable to create asset ${assetType}`);
    }
};

/**
 * Add a brand new asset to a scene!
 * @param parent
 * @param assetType
 * @param settings {AssetSettings}
 */
export function addAssetToContainer<T extends AssetType>(parent, assetType: T, settings = {}): GetAssetType<T> {
    assertInEnum(assetType, [
        Config.GROUP_ASSET_TYPE,
        Config.COLOR_ASSET_TYPE,
        Config.CIRCLE_ASSET_TYPE,
        Config.GRADIENT_ASSET_TYPE,
        Config.RECTANGLE_ASSET_TYPE,
        'TextAsset',
        Config.TEXT_ASSET_TYPE,
        Config.CANVAS_TEXT_ASSET_TYPE,
        Config.CANVAS_FANCY_CAPTION_TYPE,
        Config.MEDIA_ASSET_TYPE,
        Config.LOTTIE_ASSET_TYPE,
        Config.ICON_ASSET_TYPE,
    ]);
    const settingsWithDefaults = {
        updateEventSource: parent,
        updateParentOnRedraw: true, // this should be true for all, except root nodes?
        autoDraw: parent.autoDraw,
        fetchResources: parent.fetchResources,
        ...settings,
    };
    const asset = getAssetForType(assetType, parent, settingsWithDefaults);
    parent._addAsset(asset);
    return asset;
};


export const buildChildrenForSceneUsingData = (scene, data) => {
    assertObject(data);
    assertHasOwnProperties(data, ['children']);

    const initAutoDraw = scene.autoDraw;
    scene.autoDraw = false;

    const childrenOfAsset = { 'scene': [] };

    // Note that the order of when our components are created is crucial here.
    // We are iterating from leafs to root.
    // Before load data we have to append children to hierarchy
    data.children.reverse().forEach((assetData) => {
        const settings = {
            name: assetData.name,
            id: assetData.id,
            updateEventSource: scene,
            updateParentOnRedraw: true, // this should be true for all, except root nodes?
            autoDraw: false,
            fetchResources: scene.fetchResources,
        };
        const asset = getAssetForType(assetData.classType, scene, settings);

        if (childrenOfAsset[asset.id]) {
            childrenOfAsset[asset.id].forEach((child) => {
                asset._addAsset(child);
            });
        }

        asset.load(assetData);

        const parentId = assetData.parentId || 'scene';
        childrenOfAsset[parentId] = childrenOfAsset[parentId] || [];
        childrenOfAsset[parentId].unshift(asset);
    });

    // append scene level children
    childrenOfAsset['scene'].forEach((child) => {
        scene._addAsset(child);
    });

    scene.autoDraw = initAutoDraw;
};
